// Core
import { Controller } from 'stimulus';

export default class extends Controller {
  static targets = ['editField', 'fileWrapper', 'fileInput', 'fileLabel', 'fileName'];

  // Target declarations
  declare readonly editFieldTargets: HTMLElement[];
  declare readonly fileWrapperTargets: HTMLElement[];
  declare readonly fileInputTargets: HTMLInputElement[];
  declare readonly fileLabelTargets: HTMLElement[];
  declare readonly fileNameTargets: HTMLElement[];

  connect() {
    this.setupFileInputs();
    this.setupInputFocusEffects();
  }

  setupFileInputs() {
    // Query all file wrappers within this controller's scope
    const fileWrappers = this.element.querySelectorAll('.file-upload-wrapper');
    fileWrappers.forEach((wrapper) => {
      const fileInput = wrapper.querySelector('.file-input-hidden') as HTMLInputElement;
      const fileLabel = wrapper.querySelector('.file-upload-label') as HTMLElement;
      const fileName = wrapper.querySelector('.file-name') as HTMLElement;

      if (fileInput && fileLabel && fileName) {
        // Make the entire wrapper clickable
        wrapper.addEventListener('click', (e) => {
          // Don't trigger if clicking on the file input itself
          if (e.target !== fileInput) {
            e.preventDefault();
            fileInput.click();
          }
        });

        // Update UI when file(s) are selected
        fileInput.addEventListener('change', (e) => {
          const target = e.target as HTMLInputElement;
          if (target.files && target.files.length > 0) {
            const files = Array.from(target.files);
            const fileCount = files.length;
            if (fileCount === 1) {
              fileName.textContent = files[0].name;
              fileLabel.innerHTML = '<i class="fa fa-check-circle"></i> File selected';
            } else {
              // Show first few file names and count
              const maxDisplay = 2;
              const displayNames = files.slice(0, maxDisplay).map(f => f.name);
              const remaining = fileCount - maxDisplay;
              if (remaining > 0) {
                fileName.textContent = `${displayNames.join(', ')} and ${remaining} more file${remaining > 1 ? 's' : ''}`;
              } else {
                fileName.textContent = files.map(f => f.name).join(', ');
              }
              fileLabel.innerHTML = `<i class="fa fa-check-circle"></i> ${fileCount} files selected`;
            }
            fileName.style.display = 'block';
            (fileLabel as HTMLElement).style.color = '#3f83c1';
            wrapper.classList.add('has-file');
          } else {
            fileName.style.display = 'none';
            fileLabel.innerHTML = '<i class="fa fa-cloud-upload"></i> Click to upload file(s) here';
            (fileLabel as HTMLElement).style.color = '#666';
            wrapper.classList.remove('has-file');
          }
        });
      }
    });
  }

  setupInputFocusEffects() {
    const inputs = this.element.querySelectorAll('input, select, textarea');
    inputs.forEach((input) => {
      input.addEventListener('focus', () => {
        (input as HTMLElement).style.borderColor = '#3f83c1';
      });
      input.addEventListener('blur', () => {
        (input as HTMLElement).style.borderColor = '#ddd';
      });
    });
  }

  editAssessment(event: Event) {
    event.preventDefault();
    const target = event.currentTarget as HTMLElement;
    const assessmentType = target.dataset.assessmentType;
    if (!assessmentType) return;

    const card = this.element.closest('.row.card') as HTMLElement;
    if (!card) return;

    const editButtons = card.querySelectorAll('.edit-assessment');
    const saveButtons = card.querySelectorAll('.save-assessment');
    const cancelButtons = card.querySelectorAll('.cancel-assessment');
    const displayValues = card.querySelectorAll('.display-value');
    const editFields = card.querySelectorAll('.edit-field');
    const deleteAttachments = card.querySelectorAll('.delete-attachment');

    editButtons.forEach((btn) => (btn as HTMLElement).style.display = 'none');
    saveButtons.forEach((btn) => (btn as HTMLElement).style.display = 'inline-block');
    cancelButtons.forEach((btn) => (btn as HTMLElement).style.display = 'flex');
    displayValues.forEach((val) => (val as HTMLElement).style.display = 'none');
    editFields.forEach((field) => (field as HTMLElement).style.display = 'block');
    deleteAttachments.forEach((btn) => (btn as HTMLElement).style.display = 'flex');

    // Reinitialize file inputs for newly visible fields
    this.setupFileInputs();
    this.setupInputFocusEffects();
  }

  cancelEdit(event: Event) {
    event.preventDefault();
    const target = event.currentTarget as HTMLElement;
    const assessmentType = target.dataset.assessmentType;
    if (!assessmentType) return;

    const card = this.element.closest('.row.card') as HTMLElement;
    if (!card) return;

    const editButtons = card.querySelectorAll('.edit-assessment');
    const saveButtons = card.querySelectorAll('.save-assessment');
    const cancelButtons = card.querySelectorAll('.cancel-assessment');
    const displayValues = card.querySelectorAll('.display-value');
    const editFields = card.querySelectorAll('.edit-field');
    const deleteAttachments = card.querySelectorAll('.delete-attachment');

    editButtons.forEach((btn) => (btn as HTMLElement).style.display = 'inline-block');
    saveButtons.forEach((btn) => (btn as HTMLElement).style.display = 'none');
    cancelButtons.forEach((btn) => (btn as HTMLElement).style.display = 'none');
    displayValues.forEach((val) => (val as HTMLElement).style.display = 'block');
    editFields.forEach((field) => (field as HTMLElement).style.display = 'none');
    deleteAttachments.forEach((btn) => (btn as HTMLElement).style.display = 'none');
  }

  saveAssessment(event: Event) {
    event.preventDefault();
    const target = event.currentTarget as HTMLElement;
    const assessmentType = target.dataset.assessmentType;
    if (!assessmentType) return;

    const card = this.element.closest('.row.card') as HTMLElement;
    if (!card) return;

    const itemId = card.dataset.itemId;
    if (!itemId) return;

    const formData = new FormData();

    // Collect all edited inputs/selects
    card.querySelectorAll('.edit-field input, .edit-field select, .edit-field textarea').forEach((input: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement) => {
      if ((input as HTMLInputElement).type === 'file') {
        const fileInput = input as HTMLInputElement;
        if (fileInput.files && fileInput.files.length > 0) {
          // Append all selected files (name should use [] in the view)
          Array.from(fileInput.files).forEach((file) => {
            formData.append(input.name, file);
          });
        }
      } else if (input.tagName === 'SELECT' && (input as HTMLSelectElement).multiple) {
        // Handle multiselect - append each selected value
        const select = input as HTMLSelectElement;
        const selectedOptions = Array.from(select.selectedOptions);
        selectedOptions.forEach((option) => {
          formData.append(input.name, option.value);
        });
      } else {
        formData.append(input.name, input.value);
      }
    });

    // Collect all hidden fields (assessment_data)
    card.querySelectorAll('.edit-field input[type="hidden"]').forEach((input: HTMLInputElement) => {
      formData.append(input.name, input.value);
    });

    // Send AJAX request to update assessments
    fetch(`/items/${itemId}/item_assessment_values/update_assessments`, {
      method: 'PATCH',
      headers: {
        'X-CSRF-Token': this.getCsrfToken(),
        'Accept': 'application/json'
      },
      body: formData
    })
    .then(resp => {
      if (resp.ok) {
        window.location.reload();
      } else {
        resp.json().then((data: any) => {
          alert(`Error: ${data.error || 'Failed to save assessment values'}`);
        }).catch(() => {
          alert('Error saving assessment values');
        });
      }
    })
    .catch(err => {
      console.error(err);
      alert('An error occurred while saving assessment values');
    });
  }

  private getCsrfToken(): string {
    const token = document.querySelector("meta[name='csrf-token']") as HTMLMetaElement;
    return token ? token.getAttribute('content') || '' : '';
  }

  deleteAttachment(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    
    if (!confirm('Are you sure you want to remove this attachment?')) {
      return;
    }

    const button = event.currentTarget as HTMLElement;
    const itemId = button.dataset.itemId;
    const attributeId = button.dataset.attributeId;
    const attachmentUrl = button.dataset.attachmentUrl;
    const container = button.closest('.file-preview-container');

    // Send attachment_url so server can remove the specific attachment
    const url = attachmentUrl ? `/items/${itemId}/item_assessment_values/destroy_attachment/${attributeId}?attachment_url=${encodeURIComponent(attachmentUrl)}` : `/items/${itemId}/item_assessment_values/destroy_attachment/${attributeId}`;

    fetch(url, {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': this.getCsrfToken(),
        'Accept': 'application/json'
      }
    })
    .then(resp => {
      if (resp.ok) {
        if (container) {
          // Remove the specific attachment container
          container.remove();
          
          // Check if there are any remaining attachments
          const displayValue = container.closest('.display-value');
          if (displayValue) {
            const remainingContainers = displayValue.querySelectorAll('.file-preview-container');
            if (remainingContainers.length === 0) {
              // No more attachments, show "No Attachments"
              const dFlex = displayValue.querySelector('.d-flex');
              if (dFlex) {
                dFlex.remove();
              }
              const noAttachments = document.createElement('span');
              noAttachments.textContent = 'No Attachments';
              noAttachments.className = 'display-value';
              displayValue.innerHTML = '';
              displayValue.appendChild(noAttachments);
            }
          }
        } else {
          // Fallback: reload if container not found
          window.location.reload();
        }
      } else {
        alert('Error removing attachment');
      }
    })
    .catch(err => {
      console.error(err);
      alert('Error removing attachment');
    });
  }
}
